import { Component, OnInit, ViewChild, ElementRef, Inject, ChangeDetectorRef } from '@angular/core';
import { FormGroup, AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatSnackBar } from '@angular/material';
import { AuthStore } from 'src/app/stores/auth/auth.store';
import { ActivityStore } from 'src/app/stores/resources/activity.store';
import { DataService } from 'src/app/services/data.service';
import { IActivity, IVocabulary } from 'src/app/interfaces/resource';
import { DISPLAY_STATUS_OBJECT, STATUS_OBJECT } from 'src/app/dummy/status';
import { ConvertService, pushObjArray } from 'src/app/services/convert.service';
import { COLLECTION_NAME, FILE_FOLDER } from 'src/app/dummy/app';
import { Observable } from 'rxjs';
import { FireStorageService } from 'src/app/services/fire-storage.service';
import { Router } from '@angular/router';
import WaveSurfer from 'wavesurfer.js';

@Component({
  selector: 'app-add-word-pronunciation',
  templateUrl: './add-word-pronunciation.component.html',
  styleUrls: ['./add-word-pronunciation.component.scss']
})
export class AddWordPronunciationComponent implements OnInit {
  @ViewChild('focusInput', {static:false}) inputEl: ElementRef;
  @ViewChild('soundFile', { static: false }) soundFile: ElementRef;
  @ViewChild('photoFile',{static:false}) photoFile: ElementRef;

  TITLE = "Create word pronunciation"

  form: FormGroup;
  word: AbstractControl;
  phonetic: AbstractControl;
  pos: AbstractControl;
  definition: AbstractControl;
  synonyms: AbstractControl;
  examples: AbstractControl;

  processNew: boolean = false;
  processClose: boolean = false;
  error: any = null;
  isCreate: boolean = false;

  SELECTED_PICTURE_FILE: any;
  SELECTED_SOUND_FILE: any;
  uploadPercent: Observable<number>
  START_UPLOAD: boolean = false;

  wave: WaveSurfer = null;

  constructor(
    public dialogRef: MatDialogRef<AddWordPronunciationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private auth: AuthStore,
    public store: ActivityStore,
    private snackBar: MatSnackBar,
    private ds: DataService,
    private storage: FireStorageService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    const { QUESTION } = this.data;
    this.form = this.fb.group({
      word: [QUESTION ? QUESTION.word : null, Validators.compose([Validators.required])],
      phonetic: [QUESTION ? QUESTION.phonetic : null],
      pos: [QUESTION ? QUESTION.pos : null],
      definition: [QUESTION ? QUESTION.definition : null],
      synonyms: [QUESTION ? QUESTION.synonyms : null],
      examples: [QUESTION ? QUESTION.examples : null],
    })

    if (QUESTION) {
      this.TITLE = "Edit word pronunciation";
      const { soundDownloadURL } = QUESTION;
      if(!this.wave){
        this.generateWaveform();
      }
      if (soundDownloadURL) {
        Promise.resolve().then(() => this.wave.load(soundDownloadURL));
      }
    }
  }

  generateWaveform(): void {
    Promise.resolve(null).then(() => {
      this.wave = WaveSurfer.create({
        container: '#waveform',
        waveColor: '#5FB709',
        height: 350,
        barWidth: 3,
        barHeight: 1.5,
        progressColor: '#5FB709',
        cursorWidth: 1,
        backgroundColor: 'rgba(255,255,255,.1)'
      });
      this.wave.on('ready', () => {
        this.wave.play();
      });
    });
  }

  onPreviewPressed(): void {
    if (!this.wave) {
      this.generateWaveform();
    }
    this.cdr.detectChanges();
    if (this.SELECTED_SOUND_FILE) {
      let file = this.SELECTED_SOUND_FILE;
      const reader = new FileReader();
      reader.onload = (evt: any) => {
        let blob = new window.Blob([new Uint8Array(evt.target.result)]);
        Promise.resolve().then(() => this.wave.loadBlob(blob));
      }
      reader.readAsArrayBuffer(file)
    }
  }

  onStopPressed(): void {
    this.wave.stop();
  }

  uploadFilePicture(event) {
    if (event && event.target.files.length > 0) {
      this.SELECTED_PICTURE_FILE = event.target.files[0];
      this.photoFile.nativeElement.value = "";
    }
  }

  uploadFileSound(event) {
    if (event && event.target.files.length > 0) {
      this.SELECTED_SOUND_FILE = event.target.files[0];
      this.onPreviewPressed()
      this.soundFile.nativeElement.value = "";
    }
  }

  async onSave(f: any, isDraft: boolean, isNew: boolean) {
    const { FORM, SKILL, ACTIVITY_TYPE, MODULE_KEY, ITEM, QUESTION, ACTIVITY_TYPE_OBJECT, SKILL_NAME } = this.data;
    if (!this.SELECTED_SOUND_FILE && !QUESTION) {
      alert('Please select sound file');
      return;
    }
    this.processClose = isDraft ? false : true;
    this.processNew = isDraft;
    this.error = null;
    this.isCreate = true;
    if (this.form.valid) {
      this.form.disable();
      const { word, phonetic, pos, synonyms, examples, definition } = f;
      const key = ITEM ? ITEM.key : this.ds.createId();
      const wordKey = QUESTION ? QUESTION.key : this.ds.createId();
      const { name, instruction, defaultInstruction } = FORM;
      const { user, selectedCompanyKey } = this.auth;
      const words = ITEM ? ITEM.words : [];
      const registerStatus = STATUS_OBJECT.submitted;
      const wordData: IVocabulary = {
        word: `${word}`.trim(),
        key: wordKey || this.ds.createId(),
        phonetic: phonetic || null,
        pictureDownloadURL: QUESTION ? QUESTION.pictureDownloadURL : null,
        waveFormDownloadURL: QUESTION ? QUESTION.waveFormDownloadURL : null,
        soundDownloadURL: QUESTION ? QUESTION.soundDownloadURL : null,
        waveFileDetail: QUESTION ? QUESTION.waveFileDetail : null,
        soundFileDetail: QUESTION ? QUESTION.soundFileDetail : null,
        pos: pos || null,
        synonyms: synonyms || null,
        examples: examples || null,
        definition: definition || null,
      };
      if (this.SELECTED_SOUND_FILE) {//NEW VOCABULARY
        this.START_UPLOAD = true;
        const file = this.SELECTED_SOUND_FILE;
        const fileName = file.name;
        const uploadPath = `${FILE_FOLDER.TRAINING_RESOURCES}/${this.ds.createId()}_${fileName}`;
        const uploadTask = this.storage.uploadSelectedFile(file, uploadPath);
        this.uploadPercent = uploadTask.percentageChanges();
        const files = await this.storage.getFileDetail(file, uploadPath, uploadTask)
        wordData.soundDownloadURL = files.downloadUrl;
        wordData.soundFileDetail = files;
        this.SELECTED_SOUND_FILE = null;
        this.soundFile.nativeElement.value = "";
        this.START_UPLOAD = false;
      }

      if (this.SELECTED_PICTURE_FILE) {//NEW VOCABULARY
        this.START_UPLOAD = true;
        const file = this.SELECTED_PICTURE_FILE;
        const fileName = file.name;
        const uploadPath = `${FILE_FOLDER.TRAINING_RESOURCES}/${this.ds.createId()}_${fileName}`;
        const uploadTask = this.storage.uploadSelectedFile(file, uploadPath);
        this.uploadPercent = uploadTask.percentageChanges();
        const files = await this.storage.getFileDetail(file, uploadPath, uploadTask)
        wordData.pictureDownloadURL = files.downloadUrl;
        wordData.waveFormDownloadURL = files.downloadUrl;
        wordData.waveFileDetail = files;
        this.SELECTED_PICTURE_FILE = null;
        this.photoFile.nativeElement.value = "";
        this.START_UPLOAD = false;
      }

      const WORDS_DATA = pushObjArray(words || [], wordData)
      const activity: IActivity = {
        key: key,
        status: DISPLAY_STATUS_OBJECT.ACTIVE,
        isDelete: false,
        created_at: ITEM ? ITEM.created_at : new Date(),
        created_by: ITEM ? ITEM.created_by : user.uid,
        updated_at: new Date(),
        updated_by: user.uid,
        date_key: ITEM ? ITEM.date_key : ConvertService.dateKey(),
        page_key: ConvertService.pageKey(),
        keyword: null,
        organizationRef: this.ds.companyRef().doc(selectedCompanyKey).ref,
        organizationKey: selectedCompanyKey,
        created_byRef: ITEM ? ITEM.created_byRef : this.ds.userProfileRef().doc(user.uid).ref,
        updated_byRef: this.ds.userProfileRef().doc(user.uid).ref,
        register_status: registerStatus,
        //fields
        name: name || null,
        instruction: instruction || null,
        defaultInstruction: defaultInstruction || false,
        words: WORDS_DATA,
        hasSubActivity: true,
        //module
        skillKey: SKILL,
        skillRef: this.ds.skillRef().doc(SKILL).ref,
        skillName: SKILL_NAME,
        activityTypeKey: ACTIVITY_TYPE,
        activityTypeRef: this.ds.activityTypeRef().doc(ACTIVITY_TYPE).ref,
        activityTypeName: ACTIVITY_TYPE_OBJECT.name,
        moduleKey: MODULE_KEY,
        moduleRef: this.ds.firestoreRef().collection(COLLECTION_NAME.MODULE).doc(MODULE_KEY).ref,
      }
      this.store.addItem(activity, this.data.MODULE, true, (success, res) => {
        this.data.ITEM = activity;
        this.snackBar.open("Word pronunciation has been saved successful.", "Done", { duration: 1000 });
        if (!isNew) {
          this.dialogRef.close('no')
          return;
        }
        this.form.reset();
        this.form.enable();
        this.processNew = false;
        this.processClose = false;
        this.SELECTED_PICTURE_FILE = null;
        this.SELECTED_SOUND_FILE = null;
        this.START_UPLOAD = false;
        this.photoFile.nativeElement.value = "";
        this.soundFile.nativeElement.value = ""
        this.isCreate = false;
        this.inputEl.nativeElement.focus()
        this.router.navigate([`/modules/activity-type/${ACTIVITY_TYPE}/${SKILL}/${MODULE_KEY}/${activity.key}`])
      })
    }
    else {
      this.processNew = false;
      this.processClose = false;
      this.isCreate = false;
      this.form.enable();
    }
  }
}